// src/Pages/TeamDetails.js
import { colors as themeColors } from '../utils/theme';
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Container, Paper, Box, Typography, Grid, Button, CircularProgress, Snackbar, Alert, TableContainer, IconButton, TextField, Tooltip, } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";

const TeamDetails_v1 = ({ loggedInUser, companyData }) => {
    // Retrieve team leader data from location state (passed from previous page)
    const { state } = useLocation();
    const navigate = useNavigate();
    const leaderId = state?.leaderId;
    const leader = state?.leader;
    const companyId = loggedInUser?.companyId;

    // -------------------- STATE MANAGEMENT --------------------
    const [allUsers, setAllUsers] = useState([]);       // Available users (current page)
    const [teamMembers, setTeamMembers] = useState([]);   // Currently assigned team members
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");

    // Loading states for API calls
    const [loadingAllUsers, setLoadingAllUsers] = useState(false);
    const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);

    // -------------------- PAGINATION & SEARCH STATE --------------------
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(0);
    const usersLimit = 5; // Number of users per page
    const [totalUsers, setTotalUsers] = useState(0); // Total available users (from backend)

    // const path = process.env.REACT_APP_path || "http://103.251.218.254:3100";
    const path = 'http://192.168.1.248:3103';

    // -------------------- FETCH ALL USERS WITH SEARCH & PAGINATION --------------------
    const fetchAllUsers = () => {
        setLoadingAllUsers(true);
        const offset = currentPage * usersLimit;
        axios
            .get(`${path}/api/allusers`, {
                params: { search: searchTerm, limit: usersLimit, offset, companyId },
            })
            .then((res) => {
                console.log("API Response:", res.data); // Debugging
                if (res.data.success) {
                    setAllUsers(res.data.data);
                    setTotalUsers(res.data.total || 0);
                    setError("");
                } else {
                    setAllUsers([]);
                    setError("No users found.");
                }
                setLoadingAllUsers(false);
            })
            .catch(() => {
                setError("Server error while fetching users.");
                setLoadingAllUsers(false);
            });
    };

    // Re-fetch users whenever the search term or current page changes.
    useEffect(() => {
        fetchAllUsers();
    }, [path, searchTerm, currentPage]);

    // -------------------- FETCH TEAM MEMBERS --------------------
    useEffect(() => {
        if (leaderId) {
            setLoadingTeamMembers(true);
            axios
                .get(`${path}/api/teamdetails`, { params: { teamLeaderId: leaderId } })
                .then((res) => {
                    if (res.data.success) {
                        setTeamMembers(res.data.data);
                        setError("");
                    } else {
                        setTeamMembers([]);
                        setError("No team members found.");
                    }
                    setLoadingTeamMembers(false);
                })
                .catch(() => {
                    setError("Server error while fetching team details.");
                    setLoadingTeamMembers(false);
                });
        }
    }, [leaderId, path]);

    // -------------------- ACTION HANDLERS --------------------
    // Add a user to the team.
    const handleAddUser = (userIdToAdd) => {
        axios
            .post(`${path}/api/teamdetails/add`, {
                teamLeaderId: leaderId,
                userIds: [userIdToAdd],
            })
            .then((res) => {
                if (res.data.success) {
                    const userToAdd = allUsers.find((user) => user.userId === userIdToAdd);
                    if (userToAdd) {
                        setTeamMembers([...teamMembers, userToAdd]);
                        // Remove the added user from the available list
                        setAllUsers(allUsers.filter((user) => user.userId !== userIdToAdd));
                    }
                    setSuccessMessage("Team member added successfully.");
                }
            })
            .catch(() => setError("Server error while adding team member."));
    };

    // Remove a user from the team.
    // When user clicks on Remove, first check if any inquiry exists for that user.
    // If found, navigate to reassignInquiryDetails page with the inquiry id.
    // Otherwise, simply remove the user locally.
    // Remove a user from the team.
    const handleRemoveUser = (userIdToRemove) => {
        axios
            .delete(`${path}/api/teamdetails/remove`, {
                data: { teamLeaderId: leaderId, userIds: [userIdToRemove] },
            })
            .then((res) => {
                if (res.data.success) {
                    const userToRemove = teamMembers.find((user) => user.userId === userIdToRemove);
                    if (userToRemove) {
                        setTeamMembers(teamMembers.filter((user) => user.userId !== userIdToRemove));
                        // Add the removed user back to available users
                        setAllUsers([...allUsers, userToRemove]);
                    }
                    setSuccessMessage("Team member removed successfully.");
                }
            })
            .catch(() => setError("Server error while removing team member."));
    };

    // Update the search term (resets page to 0 on change).
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(0);
    };

    // -------------------- PAGINATION RENDERING --------------------
    const totalPages = Math.max(1, Math.ceil(totalUsers / usersLimit)); // Ensure at least 1 page
    const renderPagination = () => {
        if (totalPages <= 1) return null; // Hide pagination if only 1 page

        return (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                {Array.from({ length: totalPages }, (_, i) => (
                    <Button key={i} variant={i === currentPage ? "contained" : "outlined"} onClick={() => setCurrentPage(i)} sx={{ mx: 0.5 }}>
                        {i + 1}
                    </Button>
                ))}
            </Box>
        );
    };

    // -------------------- RENDER FUNCTIONS --------------------
    // Render the All Users table with a search field and pagination.
    const renderAllUsersTable = () => (
        <Paper sx={{ p: 2, boxShadow: 3, width: "100%", mx: "auto", mb: 2 }}>
            <Typography
                variant="subtitle1"
                sx={{
                    mb: 1,
                    fontSize: { xs: "0.9rem", sm: "1rem", md: "1.2rem" }, // Responsive font size
                }}
            >
                All Users (Available to Add)
            </Typography>
            <TextField
                label="Employee Name"
                variant="outlined"
                size="small"
                fullWidth
                sx={{ mb: 2 }}
                value={searchTerm}
                onChange={handleSearchChange}
            />
            {loadingAllUsers ? (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                    <CircularProgress size={24} />
                </Box>
            ) : allUsers.length > 0 ? (
                <>
                    <TableContainer sx={{ overflowX: "auto" }}>
                        <table
                            style={{
                                width: "100%",
                                tableLayout: "fixed",
                                borderCollapse: "collapse",
                            }}
                        >
                            <thead>
                                <tr style={{ borderBottom: `1px solid ${themeColors.neutral.line}` }}>
                                    <th
                                        style={{
                                            padding: "12px",
                                            width: "10%", // Decrease width of the serial number column
                                            fontSize: "0.9rem",
                                        }}
                                    >
                                        #
                                    </th>
                                    <th
                                        style={{
                                            padding: "12px",
                                            fontSize: "0.9rem",
                                            width: "30%", // Set appropriate width for other columns
                                        }}
                                    >
                                        User Name
                                    </th>
                                    <th
                                        style={{
                                            padding: "12px",
                                            fontSize: "0.9rem",
                                            width: "40%", // Adjust width for Employee Name
                                        }}
                                    >
                                        Employee Name
                                    </th>
                                    <th
                                        style={{
                                            padding: "12px",
                                            textAlign: "center",
                                            fontSize: "0.9rem",
                                            width: "20%", // Set width for the action column
                                        }}
                                    >
                                        Action
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {allUsers.map((user, index) => (
                                    <tr
                                        key={user.userId}
                                        style={{
                                            borderBottom: `1px solid ${themeColors.neutral.border}`,
                                            transition: "background-color 0.2s",
                                            cursor: "pointer",
                                        }}
                                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = themeColors.neutral.surfaceMuted)}
                                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                                    >
                                        <td
                                            style={{
                                                padding: "12px",
                                                fontSize: "0.8rem",
                                                width: "10%", // Ensure serial number column stays small
                                            }}
                                        >
                                            {index + 1}
                                        </td>
                                        <td
                                            style={{
                                                padding: "12px",
                                                fontSize: "0.8rem",
                                                width: "30%", // Adjust User Name column
                                            }}
                                        >
                                            {user.userName}
                                        </td>
                                        <td
                                            style={{
                                                padding: "12px",
                                                fontSize: "0.8rem",
                                                width: "40%", // Adjust Employee Name column
                                            }}
                                        >
                                            {user.employeeName}
                                        </td>
                                        <td
                                            style={{
                                                padding: "12px",
                                                textAlign: "center",
                                                fontSize: "0.8rem",
                                                width: "20%", // Adjust Action column
                                            }}
                                        >
                                            <Tooltip title="Add to Team">
                                                <IconButton color="primary" onClick={() => handleAddUser(user.userId)} aria-label="Add to Team">
                                                    <AddCircleOutlineIcon fontSize="large" />
                                                </IconButton>
                                            </Tooltip>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </TableContainer>
                    {renderPagination()}
                </>
            ) : (
                <Typography sx={{ textAlign: "center", fontStyle: "italic", color: "gray" }}>
                    {error ? "Error fetching users. Please try again later." : "Users are already assigned to a team / Not found."}
                </Typography>
            )}
        </Paper>
    );

    // Render the Team Members table.
    const renderTeamMembersTable = () => (
        <Paper sx={{ p: 2, boxShadow: 3, width: "100%", mx: "auto", mb: 2 }}>
            <Typography
                variant="subtitle1"
                sx={{
                    mb: 1,
                    fontSize: { xs: "0.9rem", sm: "1rem", md: "1.2rem" }, // Responsive font size
                }}
            >
                Team Members
            </Typography>
            {loadingTeamMembers ? (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                    <CircularProgress size={24} />
                </Box>
            ) : teamMembers.length > 0 ? (
                <TableContainer sx={{ overflowX: "auto" }}>
                    <table
                        style={{
                            width: "100%",
                            tableLayout: "fixed",
                            borderCollapse: "collapse",
                        }}
                    >
                        <thead>
                            <tr style={{ borderBottom: `1px solid ${themeColors.neutral.line}` }}>
                                <th
                                    style={{
                                        padding: "12px",
                                        width: "10%", // Decrease width of the serial number column
                                        fontSize: "0.9rem",
                                    }}
                                >
                                    #
                                </th>
                                <th
                                    style={{
                                        padding: "12px",
                                        fontSize: "0.9rem",
                                        width: "30%", // Set appropriate width for other columns
                                    }}
                                >
                                    User Name
                                </th>
                                <th
                                    style={{
                                        padding: "12px",
                                        fontSize: "0.9rem",
                                        width: "40%", // Adjust width for Employee Name
                                    }}
                                >
                                    Employee Name
                                </th>
                                <th
                                    style={{
                                        padding: "12px",
                                        textAlign: "center",
                                        fontSize: "0.9rem",
                                        width: "20%", // Set width for the action column
                                    }}
                                >
                                    Action
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {teamMembers.map((user, index) => (
                                <tr
                                    key={user.userId}
                                    style={{
                                        borderBottom: `1px solid ${themeColors.neutral.border}`,
                                        transition: "background-color 0.2s",
                                        cursor: "pointer",
                                    }}
                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = themeColors.neutral.surfaceMuted)}
                                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                                >
                                    <td
                                        style={{
                                            padding: "12px",
                                            fontSize: "0.8rem",
                                            width: "10%", // Ensure serial number column stays small
                                        }}
                                    >
                                        {index + 1}
                                    </td>
                                    <td
                                        style={{
                                            padding: "12px",
                                            fontSize: "0.8rem",
                                            width: "30%", // Adjust User Name column
                                        }}
                                    >
                                        {user.userName}
                                    </td>
                                    <td
                                        style={{
                                            padding: "12px",
                                            fontSize: "0.8rem",
                                            width: "40%", // Adjust Employee Name column
                                        }}
                                    >
                                        {user.employeeName}
                                    </td>
                                    <td
                                        style={{
                                            padding: "12px",
                                            textAlign: "center",
                                            fontSize: "0.8rem",
                                            width: "20%", // Adjust Action column
                                        }}
                                    >
                                        <Tooltip title="Remove from Team">
                                            <IconButton color="error" onClick={() => handleRemoveUser(user.userId)} aria-label="Remove from Team">
                                                <RemoveCircleOutlineIcon fontSize="large" />
                                            </IconButton>
                                        </Tooltip>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </TableContainer>
            ) : (
                <Typography>No team members found.</Typography>
            )}
        </Paper>
    );

    // Renders navigation buttons ("Close" and "Back") at the bottom.
    const renderNavigationButtons = () => (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, gap: 1 }}>
            <Button variant="contained" startIcon={<CloseIcon />} onClick={() => navigate("/main/home")}>
                Close
            </Button>
            <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={() => navigate("/main/v1/teamleaderdetails")}>
                Back
            </Button>
        </Box>
    );

    // -------------------- COMPONENT RENDER --------------------
    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Paper elevation={5} sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom>
                    Team Details for Leader: {leader ? `${leader.employeeName} (${leader.userName})` : leaderId}
                </Typography>
                {error && <Alert severity="error">{error}</Alert>}
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        {renderAllUsersTable()}
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {renderTeamMembersTable()}
                    </Grid>
                </Grid>
            </Paper>
            {renderNavigationButtons()}
            <Snackbar open={!!successMessage} autoHideDuration={3000} onClose={() => setSuccessMessage("")} message={successMessage} />
        </Container>
    );
};

export default TeamDetails_v1;